import React from "react";
import PropTypes from "prop-types";
import { StatusPill, CodChip, partnerName, isCodDelivery, codAmount, formatCurrency, isAwbPending } from "../shared";
import { formatShortDate } from "../../../utils/format";
import { getDeliveryTrackingLink, copyTrackingLink } from "../../../../components/delivery/helper";

// Every delivery booked against one order, opened from the Orders tab's Deliveries count.
//
// Read-only: the rows are the order's `deliveries` (the hasMany GET /userOrder/admin
// already joins), so nothing is fetched here. Acting on a shipment — labels, cancel, mark
// delivered — happens in the Delivery Queue, which owns those actions.
//
// Newest first: when an order has been re-shipped, the live delivery is the one people
// are looking for, and the cancelled attempts are history beneath it.
function OrderDeliveriesModal({ order, onClose }) {
    const deliveries = [...(order?.deliveries || [])].sort((a, b) => (b.id || 0) - (a.id || 0));

    return (
        <div className="flex max-w-[760px] min-w-[520px] flex-col gap-[0.9rem]">
            <div className="flex items-center justify-between">
                <span className="text-[1.5rem] font-bold">Deliveries</span>
                <span className="text-[1.2rem]">
                    Order ID: <span className="text-[1.2rem] font-semibold">{`#${order?.id ?? "—"}`}</span>
                </span>
            </div>
            <hr />

            {deliveries.length === 0 ? (
                <p className="text-[1.2rem] text-gray-500">No deliveries have been created for this order.</p>
            ) : (
                <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto pr-1">
                    {deliveries.map((delivery) => (
                        <DeliveryCard key={delivery.id} delivery={delivery} />
                    ))}
                </div>
            )}

            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl border border-gray-300 px-5 py-2 text-[1.2rem] font-semibold text-gray-700 hover:bg-gray-50"
                >
                    Close
                </button>
            </div>
        </div>
    );
}

function DeliveryCard({ delivery }) {
    const trackingLink = getDeliveryTrackingLink(delivery);
    const labelUrl = delivery?.courierDetails?.labelUrl;
    const isCod = isCodDelivery(delivery);

    const fields = [
        ["Delivery Partner", partnerName(delivery)],
        ["Partner Reference ID", isAwbPending(delivery) ? "AWB pending" : delivery.deliveryPartnerUniqueId || "—"],
        ["Created", delivery.createdAt ? formatShortDate(delivery.createdAt) : "—"],
        ["Pickup Date", delivery.pickupDate ? formatShortDate(delivery.pickupDate) : "—"],
        ["Delivered", delivery.deliveredAt ? formatShortDate(delivery.deliveredAt) : "—"],
        ["COD", <CodChip key="cod" isCod={isCod} />],
        ["COD Amount", isCod ? formatCurrency(codAmount(delivery)) : "—"],
    ];

    return (
        <div className="rounded-xl border border-gray-200 p-4">
            <div className="mb-3 flex items-center justify-between">
                <span className="text-[1.2rem] font-semibold">{`Delivery #${delivery.id}`}</span>
                <StatusPill status={delivery.status} />
            </div>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {fields.map(([label, value]) => (
                    <div key={label} className="flex flex-col">
                        <dt className="text-xs text-gray-500">{label}</dt>
                        <dd className="text-black">{value}</dd>
                    </div>
                ))}
            </dl>

            {(trackingLink || labelUrl) && (
                <div className="mt-3 flex flex-wrap gap-2">
                    {trackingLink && (
                        <>
                            <a
                                href={trackingLink}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-md px-2 py-1 text-xs font-medium text-[#00A48B] hover:bg-gray-50"
                            >
                                Open Tracking
                            </a>
                            <button
                                type="button"
                                onClick={() => copyTrackingLink(trackingLink)}
                                className="rounded-md px-2 py-1 text-xs font-medium text-[#00A48B] hover:bg-gray-50"
                            >
                                Copy Tracking Link
                            </button>
                        </>
                    )}
                    {labelUrl && (
                        <a
                            href={labelUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-md px-2 py-1 text-xs font-medium text-[#00A48B] hover:bg-gray-50"
                        >
                            Open Label
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

DeliveryCard.propTypes = { delivery: PropTypes.object.isRequired };

OrderDeliveriesModal.propTypes = {
    order: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};

export default OrderDeliveriesModal;
